import { useState } from "react";
import { Loader2, Mail, MapPin, Phone, Globe, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Section } from "./Section";
import { Reveal } from "./Reveal";
import { AssetImage } from "./AssetImage";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { submitLead, apiError } from "../../lib/api";
import { ASSETS, CONTACT, INDUSTRIES_WELCOME, PRICE_TABLE, waLink } from "../../config/site";

const inputCls = "bg-paper-light border-border text-ink placeholder:text-[#4A4C4A]/50 focus-visible:ring-gold h-11";

const VisitForm = () => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({ type: "site_visit", ...form });
      setDone(true);
      toast.success("Site visit request received. See you at the estate!");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="flex h-full flex-col items-center justify-center border border-gold/50 bg-gold-pale/40 p-10 text-center" data-testid="visit-form-success">
        <CheckCircle2 className="h-12 w-12 text-forest-light" strokeWidth={1.5} />
        <p className="mt-4 font-display text-2xl text-forest">Visit booked!</p>
        <p className="mt-2 text-sm text-[#4A4C4A]">Our team will call {form.phone} to confirm the timing.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="border border-border bg-paper-light p-6 sm:p-8" data-testid="site-visit-form">
      <h3 className="font-display text-2xl font-medium text-forest">Book your site visit</h3>
      <p className="mt-1 text-sm text-[#4A4C4A]">Chai is on us. The plots speak for themselves.</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="sv-name">Full name *</Label>
          <Input id="sv-name" data-testid="visit-name-input" required minLength={2} className={inputCls} value={form.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Your name" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sv-phone">Phone *</Label>
          <Input id="sv-phone" data-testid="visit-phone-input" required type="tel" className={inputCls} value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="10-digit mobile" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sv-company">Company</Label>
          <Input id="sv-company" data-testid="visit-company-input" className={inputCls} value={form.company || ""} onChange={(e) => set("company", e.target.value)} placeholder="Company name" />
        </div>
        <div className="space-y-1.5">
          <Label>Industry</Label>
          <Select value={form.industry || ""} onValueChange={(v) => set("industry", v)}>
            <SelectTrigger data-testid="visit-industry-select" className={inputCls}>
              <SelectValue placeholder="Select industry" />
            </SelectTrigger>
            <SelectContent className="bg-paper-light">
              {[...INDUSTRIES_WELCOME, "Other"].map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Plot size of interest</Label>
          <Select value={form.plot_size || ""} onValueChange={(v) => set("plot_size", v)}>
            <SelectTrigger data-testid="visit-plot-size-select" className={inputCls}>
              <SelectValue placeholder="Select size" />
            </SelectTrigger>
            <SelectContent className="bg-paper-light">
              {[...PRICE_TABLE.map((p) => p.label), "Plug & play shed (lease)", "Not sure yet"].map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sv-date">Preferred date</Label>
          <Input id="sv-date" data-testid="visit-date-input" type="date" className={inputCls} value={form.preferred_date || ""} onChange={(e) => set("preferred_date", e.target.value)} />
        </div>
      </div>
      <Button type="submit" disabled={loading} data-testid="visit-submit-button" className="mt-6 h-11 w-full bg-forest text-paper hover:bg-forest-light transition-colors duration-200">
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Book Site Visit
      </Button>
    </form>
  );
};

export const Contact = () => (
  <Section id="contact" overline="Visit us" title="The site office is open. Come see the land.">
    <div className="grid gap-10 lg:grid-cols-2 lg:gap-14">
      <Reveal>
        <div className="flex h-full flex-col">
          <div className="overflow-hidden border border-border">
            <AssetImage
              asset={ASSETS.office}
              alt="BIDC site office, NH-709 Bhiwani"
              className="h-52 w-full object-cover"
              placeholderLabel="BIDC site office"
            />
          </div>
          <ul className="mt-7 space-y-5">
            <li className="flex items-start gap-3.5">
              <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
              <span className="text-sm leading-relaxed text-[#4A4C4A]">{CONTACT.address}</span>
            </li>
            <li className="flex items-start gap-3.5">
              <Phone className="mt-0.5 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {CONTACT.phones.map((p) => (
                  <a key={p} href={`tel:+91${p}`} data-testid={`contact-phone-${p}`} className="text-sm font-medium text-forest hover:text-gold-dark transition-colors duration-150">
                    {p}
                  </a>
                ))}
              </div>
            </li>
            <li className="flex items-start gap-3.5">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-[#25D366]">
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5"><path d="M17.5 14.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.47-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.5 0 1.47 1.07 2.9 1.22 3.1.15.2 2.1 3.2 5.1 4.49.71.3 1.27.49 1.7.63.72.23 1.37.2 1.88.12.58-.09 1.76-.72 2.01-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35M12.04 21.79h-.01a9.8 9.8 0 0 1-5-1.37l-.36-.21-3.72.97.99-3.62-.23-.37a9.77 9.77 0 0 1-1.5-5.22c0-5.4 4.4-9.8 9.82-9.8a9.74 9.74 0 0 1 6.94 2.88 9.74 9.74 0 0 1 2.87 6.93c0 5.41-4.4 9.81-9.8 9.81m8.36-18.16A11.7 11.7 0 0 0 12.03 0C5.5 0 .18 5.31.18 11.83c0 2.08.55 4.12 1.58 5.92L.08 24l6.4-1.68a11.85 11.85 0 0 0 5.56 1.42h.01c6.53 0 11.85-5.31 11.85-11.83 0-3.16-1.23-6.13-3.5-8.28"/></svg>
              </span>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {[CONTACT.whatsappPrimary, CONTACT.whatsappSecondary].map((w) => (
                  <a key={w} href={waLink(w, "Namaste! Enquiry about BIDC plots.")} target="_blank" rel="noopener noreferrer" data-testid={`contact-whatsapp-${w}`} className="text-sm font-medium text-forest hover:text-[#25D366] transition-colors duration-150">
                    {w.replace("91", "")}
                  </a>
                ))}
              </div>
            </li>
            <li className="flex items-start gap-3.5">
              <Mail className="mt-0.5 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="break-all text-sm font-medium text-forest hover:text-gold-dark transition-colors duration-150">
                {CONTACT.email}
              </a>
            </li>
            <li className="flex items-start gap-3.5">
              <Globe className="mt-0.5 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
              <a href={CONTACT.websiteUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-website" className="text-sm font-medium text-forest hover:text-gold-dark transition-colors duration-150">
                {CONTACT.website}
              </a>
            </li>
          </ul>
        </div>
      </Reveal>

      <Reveal delay={0.12}>
        <VisitForm />
      </Reveal>
    </div>
  </Section>
);
